/**
 * Admin → Raids → Automatic profiles, one per server.
 *
 * Separate rows per server are non-negotiable: Modded Demise allows Lv 1-255
 * while Revival is vanilla-capped at 65, so one shared profile could never
 * suit both.
 *
 * The schedule is shown READ-ONLY on purpose — schedule editing stays where
 * it already lives.
 */
import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Zap, AlertTriangle, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { RaidProfileEditor } from "@/components/admin/RaidProfileEditor";

export default function AdminRaidsAutomatic() {
  const toast = useToast();
  const [servers, setServers] = useState([]);
  const [active, setActive] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [presence, setPresence] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/admin/raids/profile?mode=manual")
      .then(({ data }) => {
        setServers(data.servers || []);
        setActive((a) => a || data.servers?.[0]?.name || "");
      })
      .catch((e) => toast.error(apiError(e)));
  }, [toast]);

  const loadSchedule = useCallback(() => {
    api.get("/admin/raids/schedule").then(({ data }) => setSchedule(data)).catch(() => {});
    api.get("/admin/raids/presence").then(({ data }) => {
      const map = {};
      for (const row of data.servers || []) map[row.server] = row;
      setPresence(map);
    }).catch(() => {});
  }, []);

  useEffect(loadSchedule, [loadSchedule]);

  /** Validating an auto profile without waiting for the window. Fires as a
      MANUAL raid, so it pays the manual ladder — said plainly on the button.
      Refuses at 0 players because PalDefender's PalID table lazy-loads and
      returns "PalID invalid" for pals that work fine on an empty server. */
  const testProfile = async () => {
    const online = presence[active]?.online ?? presence[active]?.players ?? null;
    if (online === 0) {
      toast.error(
        "Nobody is online. PalDefender reports 'PalID invalid' for perfectly " +
        "good pals on an empty server, so this test would lie to you.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/raids/spawn?server=${encodeURIComponent(active)}`, {});
      toast.success(
        `Test raid ${data.pal_name || ""} spawned on ${active} — it pays the MANUAL ladder`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const activeMeta = servers.find((s) => s.name === active);
  const rows = (schedule?.servers || schedule?.schedule || []).filter?.(
    (r) => (r.server || r.server_name) === active) || [];

  return (
    <div className="space-y-5" data-testid="admin-raids-automatic">
      <header>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CalendarClock size={17} className="text-[var(--teal)]" />
          Automatic raids
        </h2>
        <p className="text-xs text-[var(--muted)] mt-1 max-w-3xl">
          One profile per server, because each server has its own level ceiling.
          These settings only apply to raids the scheduler fires.
        </p>
      </header>

      <div className="flex gap-2 flex-wrap" data-testid="auto-server-tabs">
        {servers.map((s) => (
          <button key={s.name} onClick={() => setActive(s.name)}
            className={`btn btn-sm ${active === s.name ? "" : "btn-secondary"}`}
            data-testid={`auto-tab-${s.name}`}>
            {s.name}
            <span className="ml-1.5 text-[10px] opacity-70">
              max Lv {s.level_cap}
            </span>
          </button>
        ))}
      </div>

      {activeMeta && (
        <p className="text-[11px] text-[var(--muted)] flex gap-1.5">
          <Info size={11} className="mt-0.5 flex-shrink-0 text-[var(--teal)]" />
          {activeMeta.modded
            ? `${activeMeta.name} is modded, so scheduled raids may go up to level ${activeMeta.level_cap}.`
            : `${activeMeta.name} is vanilla, so scheduled raids are capped at level ${activeMeta.level_cap}.`}
        </p>
      )}

      {schedule && (
        <section className="glass-card p-4" data-testid="auto-schedule-readonly">
          <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-2">
            Schedule (read-only)
          </h4>
          {rows.length === 0 ? (
            <p className="text-[11px] text-[var(--muted)]">
              No schedule rows for this server.
            </p>
          ) : (
            <ul className="text-[11px] text-[var(--muted)] space-y-1">
              {rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  {r.next_local || r.next_run || r.scheduled_date || "—"}
                  {r.fired_today != null && (
                    <span className="ml-2 opacity-70">
                      {r.fired_today ? "already fired today" : "not fired yet today"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-[var(--dim)] mt-2">
            Times and weekdays are edited where they already live — this page
            only owns what a raid looks like and pays.
          </p>
        </section>
      )}

      {active && <RaidProfileEditor mode="auto" server={active} />}

      <section className="glass-card p-4 border border-amber-400/30"
        data-testid="auto-test-panel">
        <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-1 flex items-center gap-1.5">
          <Zap size={13} className="text-amber-300" /> Test this profile
        </h4>
        <p className="text-[11px] text-amber-300 flex gap-1.5 mb-3">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          {/* One <span> wrapper: in a flex row every inline child becomes a
              flex item, which chops the sentence into columns. */}
          <span>
            Fires a raid on {active || "this server"} right now as a{" "}
            <strong>manual</strong> raid, so it pays the <strong>manual</strong>{" "}
            reward ladder, not this one. Needs at least one player online —
            PalDefender misreports good pals as invalid on an empty server.
          </span>
        </p>
        <button onClick={testProfile} disabled={busy || !active}
          className="btn btn-sm btn-secondary flex items-center gap-1.5"
          data-testid="auto-test-button">
          <Zap size={12} /> Test on {active || "…"}
        </button>
      </section>

      <RecentFailures server={active} />
    </div>
  );
}

/** Failed raid attempts for one server. Until 2026-10-04 nothing in the
    database recorded a failure, so 518 consecutive ones went unnoticed for
    seven months. Renders nothing when there are none — this page is normally
    quiet and an empty card would just add noise. */
function RecentFailures({ server }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!server) return;
    api
      .get(`/admin/raids/failures?server=${encodeURIComponent(server)}&limit=25`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null));
  }, [server]);

  const rows = data?.failures || [];
  if (!rows.length) return null;
  const { total = 0, world_not_loaded: empty = 0 } = data.rollup || {};

  return (
    <section className="glass-card p-4 border border-red-400/30"
      data-testid="auto-failures-card">
      <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-1 flex items-center gap-1.5">
        <AlertTriangle size={13} className="text-red-300" /> Recent failures
      </h4>
      <p className="text-[11px] text-[var(--muted)] mb-3" data-testid="auto-failures-rollup">
        {empty} of {total} failures happened with an empty world — no player had
        loaded it since the server restarted, so PalDefender had no PalID table.
      </p>
      <ul className="space-y-2">
        {rows.map((f) => (
          <li key={f.id} className="text-[11px] border-b border-white/5 pb-2"
            data-testid="auto-failure-row">
            <div className="flex flex-wrap gap-x-2 gap-y-0.5 items-baseline">
              <span className="text-[var(--muted)]">
                {String(f.attempted_at || "").replace("T", " ").slice(0, 19)}
              </span>
              <span className="text-[var(--bone)]">{f.pal_name || "—"}</span>
              {f.level != null && <span className="opacity-60">Lv {f.level}</span>}
              <span className="px-1.5 rounded bg-white/5 text-[10px] uppercase tracking-wider">
                {f.trigger_source}
              </span>
              <span className="px-1.5 rounded bg-red-400/10 text-red-300 text-[10px] uppercase tracking-wider">
                {f.stage}
              </span>
              {f.world_loaded === 0 && (
                <span className="text-[10px] text-amber-300">empty world</span>
              )}
            </div>
            <div className="text-[var(--muted)] mt-0.5">{f.reason}</div>
            {f.paldefender_log && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[10px] text-[var(--teal)]">
                  PalDefender log
                </summary>
                <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[10px] text-[var(--dim)]">
                  {f.paldefender_log}
                </pre>
              </details>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
